import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ThemeProvider } from "@/contexts/ThemeContext";

// Pages
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Accept from "@/pages/Accept";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import PageBuilder from "@/pages/PageBuilder";
import PromoPage from "@/pages/PromoPage";
import PublicPage from "@/pages/PublicPage";
import SubdomainPage from "@/pages/SubdomainPage";
import Analytics from "@/pages/Analytics";
import GlobalAnalytics from "@/pages/GlobalAnalytics";
import AdminPanel from "@/pages/AdminPanel";
import Domains from "@/pages/Domains";
import Settings from "@/pages/Settings";
import Verification from "@/pages/Verification";
import RandomCover from "@/pages/RandomCover";
import Support from "@/pages/Support";
import Chart from "@/pages/Chart";
import { getAppDomainUrl, getSubdomain, isAppSubdomain, isLocalHost } from "@/utils/subdomain";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Storage keys
const TOKEN_KEY = "token";
const USER_KEY = "user";

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const api = axios.create({
  baseURL: API,
  // Send/receive the httpOnly auth cookie on cross-site requests
  withCredentials: true,
});

// Read a cookie value by name (for the CSRF double-submit token)
const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
};

// Add auth header interceptor - automatically adds token to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // CSRF double-submit: echo the csrf_token cookie on state-changing requests
  const method = (config.method || 'get').toLowerCase();
  if (!['get', 'head', 'options'].includes(method)) {
    const csrf = getCookie('csrf_token');
    if (csrf) {
      config.headers['X-CSRF-Token'] = csrf;
    }
  }
  return config;
});

// Add response interceptor - handle 401 errors (expired/invalid token)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear storage
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      // Redirect to login if not already there
      if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Helper functions for localStorage
const saveAuthData = (token, user) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

const clearAuthData = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

const getStoredUser = () => {
  try {
    const userStr = localStorage.getItem(USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  } catch {
    return null;
  }
};

const getStoredToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Check admin access for owner, admin, and moderator roles
  if (adminOnly && !["owner", "admin", "moderator"].includes(user.role)) {
    return <Navigate to="/multilinks" replace />;
  }
  
  return children;
};

const LegacyPageEditRedirect = () => {
  const pageId = window.location.pathname.split("/")[2];
  return <Navigate to={`/multilinks/${pageId}/edit`} replace />;
};

const AppHomeRedirect = () => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={user?.role === "admin" || user?.role === "owner" ? "/admin" : "/multilinks"} replace />;
};

const ExternalRedirect = ({ to }) => {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
    </div>
  );
};

function App() {
  // Initialize user from localStorage for instant UI (no flash)
  const [user, setUser] = useState(() => getStoredUser());
  const [loading, setLoading] = useState(true);
  
  // Check if we're on a subdomain
  const subdomain = getSubdomain();
  const isApp = isAppSubdomain();
  const redirectAppRoutes = !isLocalHost();

  // On mount: verify token with backend and update user data
  useEffect(() => {
    const initAuth = async () => {
      const token = getStoredToken();
      const storedUser = getStoredUser();
      
      if (token && storedUser) {
        // User data exists in localStorage - show UI immediately
        setUser(storedUser);
        
        // Verify token and refresh user data in background
        try {
          const response = await api.get("/auth/me");
          const freshUser = response.data;
          // Update localStorage and state with fresh data
          localStorage.setItem(USER_KEY, JSON.stringify(freshUser));
          setUser(freshUser);
        } catch (error) {
          // Token invalid/expired - clear everything
          console.warn("Token validation failed:", error.message);
          clearAuthData();
          setUser(null);
        }
      } else if (token) {
        // Token exists but no user data - fetch user
        try {
          const response = await api.get("/auth/me");
          saveAuthData(token, response.data);
          setUser(response.data);
        } catch (error) {
          clearAuthData();
          setUser(null);
        }
      } else {
        // No token - ensure clean state
        clearAuthData();
        setUser(null);
      }
      
      setLoading(false);
    };
    
    initAuth();
  }, []);

  // Login function - saves both token and user to localStorage
  const login = async (email, password, language) => {
    const response = await api.post("/auth/login", { email, password, language });
    const { token, user: userData } = response.data;
    
    // Save to localStorage
    saveAuthData(token, userData);
    
    // Update state
    setUser(userData);
    
    return response.data;
  };

  // Register function - may require email OTP verification before login
  const register = async (email, username, password, language) => {
    const response = await api.post("/auth/register", { email, username, password, language });
    const data = response.data;
    
    // Email confirmation required: no token yet, redirect to /accept
    if (data.requires_verification) {
      return data;
    }
    
    const { token, user: userData } = data;
    saveAuthData(token, userData);
    setUser(userData);
    return data;
  };

  // Verify the email OTP code and finish login
  const verifyOtp = async (email, code) => {
    const response = await api.post("/auth/verify-otp", { email, code });
    const { token, user: userData } = response.data;
    saveAuthData(token, userData);
    setUser(userData);
    return response.data;
  };

  // Resend the email OTP code
  const resendOtp = async (email, language) => {
    const response = await api.post("/auth/resend-otp", { email, language });
    return response.data;
  };

  // Logout function - clears the httpOnly cookie, localStorage and state
  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Ignore network errors on logout
    }
    clearAuthData();
    setUser(null);
  };

  // Refresh user data from backend
  const refreshUser = async () => {
    try {
      const response = await api.get("/auth/me");
      const freshUser = response.data;
      
      // Update localStorage
      localStorage.setItem(USER_KEY, JSON.stringify(freshUser));
      
      // Update state
      setUser(freshUser);
      
      return freshUser;
    } catch (error) {
      console.error("Failed to refresh user:", error.message);
      throw error;
    }
  };

  // Check if user is authenticated
  const isAuthenticated = !!user && !!getStoredToken();

  // If on subdomain, show SubdomainPage only
  if (subdomain) {
    return (
      <LanguageProvider>
        <AuthContext.Provider value={{ 
          user, 
          login, 
          register, 
          verifyOtp,
          resendOtp,
          logout, 
          loading, 
          refreshUser,
          isAuthenticated 
        }}>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<SubdomainPage />} />
              <Route path="/:slug" element={<SubdomainPage />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </AuthContext.Provider>
      </LanguageProvider>
    );
  }

  if (isApp) {
    return (
      <ThemeProvider>
        <LanguageProvider>
          <AuthContext.Provider value={{ 
            user, 
            login, 
            register, 
            verifyOtp,
            resendOtp,
            logout, 
            loading, 
            refreshUser,
            isAuthenticated 
          }}>
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<AppHomeRedirect />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/accept" element={<Accept />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/multilinks" element={
                  <ProtectedRoute><Dashboard /></ProtectedRoute>
                } />
                <Route path="/random-cover" element={
                  <ProtectedRoute><RandomCover /></ProtectedRoute>
                } />
                <Route path="/analytics" element={
                  <ProtectedRoute><GlobalAnalytics /></ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <ProtectedRoute><Settings /></ProtectedRoute>
                } />
                <Route path="/domains" element={
                  <ProtectedRoute><Domains /></ProtectedRoute>
                } />
                <Route path="/verification" element={
                  <ProtectedRoute><Verification /></ProtectedRoute>
                } />
                <Route path="/support" element={
                  <ProtectedRoute><Support /></ProtectedRoute>
                } />
                <Route path="/multilinks/create" element={
                  <ProtectedRoute><PageBuilder /></ProtectedRoute>
                } />
                <Route path="/multilinks/:pageId/edit" element={
                  <ProtectedRoute><PageBuilder /></ProtectedRoute>
                } />
                <Route path="/multilinks/:pageId/promo" element={
                  <ProtectedRoute><PromoPage /></ProtectedRoute>
                } />
                <Route path="/page/new" element={<Navigate to="/multilinks/create" replace />} />
                <Route path="/page/:pageId/edit" element={<LegacyPageEditRedirect />} />
                <Route path="/analytics/:pageId" element={
                  <ProtectedRoute><Analytics /></ProtectedRoute>
                } />
                <Route path="/admin" element={
                  <ProtectedRoute adminOnly><AdminPanel /></ProtectedRoute>
                } />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
            <Toaster position="top-right" richColors />
          </AuthContext.Provider>
        </LanguageProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthContext.Provider value={{ 
          user, 
          login, 
          register, 
          verifyOtp,
          resendOtp,
          logout, 
          loading, 
          refreshUser,
          isAuthenticated 
        }}>
          <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/chart" element={<Chart />} />
            <Route path="/login" element={redirectAppRoutes ? <ExternalRedirect to={getAppDomainUrl("/login")} /> : <Login />} />
            <Route path="/register" element={redirectAppRoutes ? <ExternalRedirect to={getAppDomainUrl("/register")} /> : <Register />} />
            <Route path="/accept" element={redirectAppRoutes ? <ExternalRedirect to={getAppDomainUrl("/accept")} /> : <Accept />} />
            <Route path="/forgot-password" element={redirectAppRoutes ? <ExternalRedirect to={getAppDomainUrl("/forgot-password")} /> : <ForgotPassword />} />
            <Route path="/reset-password" element={redirectAppRoutes ? <ExternalRedirect to={`${getAppDomainUrl("/reset-password")}${window.location.search}`} /> : <ResetPassword />} />
            
            {/* Protected Routes */}
            <Route path="/multilinks" element={
              redirectAppRoutes ? <ExternalRedirect to={getAppDomainUrl("/multilinks")} /> : <ProtectedRoute><Dashboard /></ProtectedRoute>
            } />
            <Route path="/random-cover" element={
              redirectAppRoutes ? <ExternalRedirect to={getAppDomainUrl("/random-cover")} /> : <ProtectedRoute><RandomCover /></ProtectedRoute>
            } />
            <Route path="/analytics" element={
              redirectAppRoutes ? <ExternalRedirect to={getAppDomainUrl("/analytics")} /> : <ProtectedRoute><GlobalAnalytics /></ProtectedRoute>
            } />
          <Route path="/settings" element={
            redirectAppRoutes ? <ExternalRedirect to={getAppDomainUrl("/settings")} /> : <ProtectedRoute><Settings /></ProtectedRoute>
          } />
          <Route path="/domains" element={
            redirectAppRoutes ? <ExternalRedirect to={getAppDomainUrl("/domains")} /> : <ProtectedRoute><Domains /></ProtectedRoute>
          } />
          <Route path="/verification" element={
            redirectAppRoutes ? <ExternalRedirect to={getAppDomainUrl("/verification")} /> : <ProtectedRoute><Verification /></ProtectedRoute>
          } />
          <Route path="/support" element={
            redirectAppRoutes ? <ExternalRedirect to={getAppDomainUrl("/support")} /> : <ProtectedRoute><Support /></ProtectedRoute>
          } />
          <Route path="/multilinks/create" element={
            redirectAppRoutes ? <ExternalRedirect to={getAppDomainUrl("/multilinks/create")} /> : <ProtectedRoute><PageBuilder /></ProtectedRoute>
          } />
          <Route path="/multilinks/:pageId/edit" element={
            redirectAppRoutes ? <ExternalRedirect to={`${getAppDomainUrl(window.location.pathname)}${window.location.search}`} /> : <ProtectedRoute><PageBuilder /></ProtectedRoute>
          } />
          <Route path="/multilinks/:pageId/promo" element={
            redirectAppRoutes ? <ExternalRedirect to={`${getAppDomainUrl(window.location.pathname)}${window.location.search}`} /> : <ProtectedRoute><PromoPage /></ProtectedRoute>
          } />
          <Route path="/page/new" element={
            redirectAppRoutes ? <ExternalRedirect to={getAppDomainUrl("/multilinks/create")} /> : <Navigate to="/multilinks/create" replace />
          } />
          <Route path="/page/:pageId/edit" element={
            redirectAppRoutes ? <ExternalRedirect to={`${getAppDomainUrl(`/multilinks/${window.location.pathname.split("/")[2]}/edit`)}${window.location.search}`} /> : <LegacyPageEditRedirect />
          } />
          <Route path="/analytics/:pageId" element={
            redirectAppRoutes ? <ExternalRedirect to={`${getAppDomainUrl(window.location.pathname)}${window.location.search}`} /> : <ProtectedRoute><Analytics /></ProtectedRoute>
          } />
          
          {/* Admin Routes */}
          <Route path="/admin" element={
            redirectAppRoutes ? <ExternalRedirect to={getAppDomainUrl("/admin")} /> : <ProtectedRoute adminOnly><AdminPanel /></ProtectedRoute>
          } />
          
          {/* Public Smart Link Page - must be last to catch /:slug */}
          <Route path="/:slug" element={<PublicPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthContext.Provider>
  </LanguageProvider>
  </ThemeProvider>
  );
}

export default App;
